import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { MaskLine, EASE } from "@/components/primitives";

const HERO_FILM = "/hero-film-v8-web.mp4";
const HERO_POSTER = "/hero-film-poster.jpg";

let introPlayed = false;

export default function Hero() {
  const [introDone, setIntroDone] = useState(introPlayed);
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  useEffect(() => {
    if (introPlayed) return;
    const t = setTimeout(() => {
      introPlayed = true;
      setIntroDone(true);
    }, 1700);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const kick = () => {
      v.play().catch(() => {});
    };
    if (v.readyState >= 3) kick();
    v.addEventListener("canplay", kick, { once: true });
    return () => v.removeEventListener("canplay", kick);
  }, []);

  const d = introPlayed ? 0.2 : 1.9;

  return (
    <section ref={ref} id="top" className="relative h-[100svh] min-h-[620px] overflow-hidden bg-ink" data-testid="hero-section">
      <AnimatePresence>
        {!introDone && (
          <motion.div
            key="intro"
            className="fixed inset-0 z-[60] flex flex-col items-center justify-center bg-paper"
            exit={{ y: "-100%" }}
            transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
            data-testid="hero-intro-overlay"
          >
            <div className="mask-line">
              <motion.img
                src="/ara-logo.png"
                alt="ARA Turnkey Projects"
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
                className="h-20 w-auto sm:h-24"
              />
            </div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.75, duration: 0.5 }}
              className="mt-5 text-[10px] font-medium uppercase tracking-[0.5em] text-ink/60"
            >
              Building experiences, not just spaces.
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div style={{ y: bgY }} className="absolute inset-0" data-testid="hero-video">
        <video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster={HERO_POSTER}
          className="h-full w-full scale-[1.06] object-cover"
          data-testid="hero-film"
        >
          <source src={HERO_FILM} type="video/mp4" />
        </video>
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/35 to-ink" />
      <div className="grain absolute inset-0" />

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 mx-auto flex h-full max-w-[1440px] flex-col justify-end px-6 pb-24 lg:px-12 lg:pb-28"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: d, duration: 0.8 }}
          className="mb-6 flex items-center gap-4 text-[11px] font-medium uppercase tracking-[0.35em] text-paper/70"
        >
          <span className="h-px w-10" style={{ backgroundColor: "#C8554F" }} />
          Design. Build. Engineer.
        </motion.p>

        <h1 className="font-serif text-[13.5vw] leading-[0.98] text-paper sm:text-[11vw] lg:text-[7.2vw]">
          <MaskLine delay={d}>Building <em className="italic">experiences</em>,</MaskLine>
          <MaskLine delay={d + 0.15}>
            not just spaces.
            <motion.span
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ delay: d + 0.7, duration: 0.9, ease: EASE }}
              className="ml-4 hidden h-[3px] w-[1.2em] origin-left align-middle sm:inline-block"
              style={{ backgroundColor: "#C8554F" }}
            />
          </MaskLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: d + 0.55, duration: 0.8, ease: EASE }}
          className="mt-7 max-w-md text-sm leading-relaxed text-paper/75"
          data-testid="hero-subline"
        >
          We believe that every experience creates a feeling. Every feeling shapes performance.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: d + 0.7, duration: 0.8, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={() => navigate("/contact")}
            data-testid="hero-cta-primary"
            className="rounded-full bg-maroon px-8 py-4 text-[12px] font-medium uppercase tracking-[0.2em] text-paper transition-transform duration-300 hover:scale-[1.04]"
          >
            Start a Project
          </button>
          <button
            onClick={() => navigate("/projects")}
            data-testid="hero-cta-secondary"
            className="group flex items-center gap-3 rounded-full border border-paper/30 px-8 py-4 text-[12px] font-medium uppercase tracking-[0.2em] text-paper transition-colors duration-300 hover:border-paper"
          >
            Explore Our Work
            <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">→</span>
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: d + 1.1, duration: 1 }}
        className="absolute bottom-8 right-6 z-10 hidden items-center gap-3 lg:flex"
      >
        <span className="text-[10px] uppercase tracking-[0.3em] text-paper/50">Scroll</span>
        <span className="relative h-14 w-px overflow-hidden bg-paper/20">
          <motion.span
            animate={{ y: ["-100%", "100%"] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: "linear" }}
            className="absolute left-0 top-0 h-full w-full"
            style={{ backgroundColor: "#C8554F" }}
          />
        </span>
      </motion.div>
    </section>
  );
}
